import React, { useState } from "react";
import { FullNote } from "../../interfaces/mortgageNote";
import ActionButton from "../buttons/ActionButton";
import useAlert from "../../hooks/useAlert";
import useUserAuth from "../../hooks/useUserAuth";
import { parsePhone } from "../../helpers/formatPhoneNumber";

interface RequestInformationFormProps {
  note: FullNote;
}

export interface RegisterFromNoteData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  noteID: number;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  message?: string;
}

const RequestInformationForm = ({ note }: RequestInformationFormProps) => {
  const initialFormData: Omit<RegisterFromNoteData, "noteID"> = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  };

  const [formData, setFormData] =
    useState<Omit<RegisterFromNoteData, "noteID">>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { setAlert } = useAlert();

  const { registerFromNote } = useUserAuth();

  const formFields = [
    { name: "firstName", label: "First Name", type: "text", required: true },
    { name: "lastName", label: "Last Name", type: "text", required: true },
    { name: "phone", label: "Phone", type: "tel", required: true },
    { name: "email", label: "Email", type: "email", required: true },
  ] as const;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Validate required fields
    formFields.forEach((field) => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label} is required`;
        isValid = false;
      }
    });

    // Validate email format if provided
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email format";
        isValid = false;
      }
    }

    // Validate phone format
    const phoneRegex = /^\d{10}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Invalid phone number format";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // For phone field, only store numeric values
    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "");
      if (
        numericValue.length > 10 &&
        (e as any).nativeEvent.inputType !== "deleteContentBackward"
      ) {
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFormSubmit = async () => {
    if (validateForm()) {
      setIsLoading(true);
      try {
        const resp = await registerFromNote({
          ...formData,
          noteID: note.id,
          phone: formData.phone.replace(/\D/g, ""),
          email: formData.email.trim().toLowerCase(),
        });
        if (resp.success) {
          setAlert({
            display: true,
            message: "Note was saved to your Saved Notes",
            type: "info",
          });
        } else {
          setAlert({
            display: true,
            message: resp.error || "Error submitting form",
            type: "error",
          });
        }
      } catch (error: any) {
        console.error("Error submitting form:", error);
        setAlert({
          display: true,
          message: error.message || "Error submitting form",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="w-full px-12 flex flex-col columns-1 gap-2">
      {formFields.map((field) => (
        <div key={field.name} className="w-full">
          <input
            type={field.type}
            name={field.name}
            placeholder={`${field.label}${field.required ? "*" : ""}`}
            value={
              field.name === "phone"
                ? parsePhone(formData[field.name])
                : formData[field.name]
            }
            onChange={handleChange}
            onKeyDown={
              field.name === "phone"
                ? (e) => {
                    // Allow: backspace, delete, tab, escape, enter
                    if (
                      [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "Escape",
                        "Enter",
                      ].includes(e.key)
                    ) {
                      return;
                    }
                    // Allow: Ctrl+A, Command+A
                    if (e.key === "a" && (e.ctrlKey || e.metaKey)) {
                      return;
                    }
                    // Only allow numeric input (0-9)
                    if (
                      !/^[0-9]$/.test(e.key) && // numbers
                      ![
                        "0",
                        "1",
                        "2",
                        "3",
                        "4",
                        "5",
                        "6",
                        "7",
                        "8",
                        "9",
                      ].includes(e.key) // numpad
                    ) {
                      e.preventDefault();
                    }
                  }
                : undefined
            }
            className={`input border-[#DDDDDD] border-[0.5px] input-bordered w-full bg-transparent input-sm rounded[1px] text-white font-semibold focus:border-white focus:border-[1px] focus:outline-none ${
              errors[field.name] ? "border-red-400" : ""
            }`}
          />
          {errors[field.name] && (
            <p className="text-red-400 text-xs font-semibold mt-1">
              {errors[field.name]}
            </p>
          )}
        </div>
      ))}

      <div className="w-full">
        <ActionButton
          label="REGISTER NOW!"
          isLoading={isLoading}
          onClick={handleFormSubmit}
        />
      </div>

      <div>
        <p className="text-xs">
          By submitting this web form, I expressly authorize Secured Investment
          Corp. to send me emails and text messages about its products and
          services using automated technology to the email address/phone number
          provided on the form above. I understand I can opt out of this consent
          at any time.
        </p>
      </div>
      <div className="text-xs">
        <p>
          Contact us at{" "}
          <a className="underline" href="tel:8008982717">
            800.898-2717
          </a>{" "}
          if you prefer not to use this form.
        </p>
      </div>
    </div>
  );
};

export default RequestInformationForm;
